import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { useInView } from "@/hooks/useInView";
import SeoHead from "@/seo/SeoHead";
import { buildAutoRepairJsonLd, buildBreadcrumbJsonLd } from "@/seo/jsonLd";
import { SITE_URL } from "@/seo/siteSeo";
import { useSiteContent } from "@/cms/SiteContentContext";
import { editableAttrs } from "@/cms/previewUtils";

const Stars = ({ count }: { count: number }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <i key={n} className={`text-amber-400 text-sm ${n <= count ? "ri-star-fill" : "ri-star-line"}`}></i>
    ))}
  </div>
);

const ReviewCard = ({ review, idx }: { review: any; idx: number }) => {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className="bg-zinc-900 border border-zinc-800 hover:border-red-600/30 transition-all duration-300 p-6"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(20px)",
        transition: `opacity 0.5s ease ${(idx % 3) * 0.1}s, transform 0.5s ease ${(idx % 3) * 0.1}s`,
      }}
    >
      <div className="flex items-center gap-4 mb-4">
        <div className="w-12 h-12 flex items-center justify-center bg-zinc-800 border border-zinc-700 flex-shrink-0">
          <i className="ri-user-3-line text-zinc-300 text-xl"></i>
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-white font-bold text-base" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              {review.name}
            </h4>
            <Stars count={review.rating} />
          </div>
          <p className="text-red-500 text-xs mt-0.5">{review.car}</p>
        </div>
      </div>
      <p className="text-zinc-400 text-sm leading-relaxed mb-3">&ldquo;{review.text}&rdquo;</p>
      <div className="flex items-center gap-2 pt-3 border-t border-zinc-800">
        <i className="ri-time-line text-zinc-600 text-xs"></i>
        <span className="text-zinc-600 text-xs">{review.date}</span>
        <span className="ml-auto text-green-500 text-xs flex items-center gap-1">
          <i className="ri-verified-badge-line text-xs"></i>
          Doğrulanmış Müşteri
        </span>
      </div>
    </div>
  );
};

export default function YorumlarPage() {
  const { ref: summaryRef, inView: summaryInView } = useInView();
  const { content } = useSiteContent();
  const page = content.reviewsPage;
  const jsonLd = [
    buildBreadcrumbJsonLd([
      { name: "Ana Sayfa", path: "/" },
      { name: "Müşteri Yorumları", path: "/yorumlar" },
    ]),
    {
      ...buildAutoRepairJsonLd(),
      url: `${SITE_URL}/yorumlar`,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "500",
        bestRating: "5",
        worstRating: "1",
      },
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#09090b" }}>
      <SeoHead title="Müşteri Yorumları" description={page.hero.description} path="/yorumlar" image={page.hero.imageSrc} jsonLd={jsonLd} />
      <Navbar />

      <section className="relative pt-36 pb-20 overflow-hidden" {...editableAttrs("reviewsPage.hero", "object")}>
        <div className="absolute inset-0 z-0">
          <img
            src={page.hero.imageSrc}
            alt="Müşteri Yorumları"
            className="w-full h-full object-cover object-top"
            {...editableAttrs("reviewsPage.hero.imageSrc", "image")}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/96 via-zinc-950/80 to-zinc-950/55"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-transparent to-zinc-950/40"></div>
        </div>
        <div className="absolute left-0 top-0 h-full w-1 bg-red-600 z-10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-4 text-sm">
            <Link to="/" className="text-zinc-500 hover:text-red-500 transition-colors cursor-pointer">
              Ana Sayfa
            </Link>
            <span className="text-zinc-700">/</span>
            <span className="text-red-500">{page.hero.title}</span>
          </div>
          <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {page.hero.badge}
          </span>
          <h1 className="text-5xl md:text-7xl font-bold text-white mt-3 leading-tight" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {page.hero.titleLines[0]}
            <br />
            {page.hero.titleLines[1]}
          </h1>
          <p className="text-zinc-300 text-lg mt-5 max-w-xl leading-relaxed">{page.hero.description}</p>
        </div>
      </section>

      <section ref={summaryRef} className="py-14 bg-zinc-900" {...editableAttrs("reviewsPage.summary", "array")}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {page.summary.map((stat, idx) => (
              <div
                key={stat.label}
                className="bg-zinc-800/40 border border-zinc-700/40 p-8 text-center"
                style={{
                  opacity: summaryInView ? 1 : 0,
                  transform: summaryInView ? "translateY(0)" : "translateY(20px)",
                  transition: `opacity 0.5s ease ${idx * 0.12}s, transform 0.5s ease ${idx * 0.12}s`,
                }}
              >
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3">
                  <i className={`${stat.icon} text-3xl ${stat.color}`}></i>
                </div>
                <div className={`text-5xl font-bold mb-1 ${stat.color}`} style={{ fontFamily: "Rajdhani, sans-serif" }}>
                  {stat.value}
                </div>
                <div className="text-white font-semibold text-base" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                  {stat.label}
                </div>
                <div className="text-zinc-500 text-xs mt-1">{stat.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-zinc-950" {...editableAttrs("reviewsPage.reviews", "array")}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              {page.section.badge}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 section-title-line-center" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              {page.section.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {page.reviews.map((review, idx) => (
              <ReviewCard key={`${review.name}-${idx}`} review={review} idx={idx} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-zinc-900" {...editableAttrs("reviewsPage.cta", "object")}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {page.cta.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-5" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {page.cta.title}
            <br />
            <span className="text-red-500">{page.cta.titleAccent}</span>
          </h2>
          <p className="text-zinc-400 text-base mb-8 max-w-xl mx-auto">{page.cta.description}</p>
          <Link
            to="/iletisim"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 font-bold text-sm tracking-widest transition-all duration-200 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "Rajdhani, sans-serif" }}
          >
            <i className="ri-calendar-check-line text-base"></i>
            {page.cta.buttonLabel}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
